import { API_BASE_URL as RUNTIME_API_BASE_URL } from '../config/runtimeConfig';

const DEFAULT_API_BASE_URL = (
  RUNTIME_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8000'
).replace(/\/+$/, '');

export const AUTH_TOKEN_KEY = 'vi_auth_token';
export const AUTH_USER_KEY = 'vi_auth_user';

export function getApiBaseUrl() {
  return DEFAULT_API_BASE_URL;
}

export function getStoredToken() {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function storeSession({ accessToken, user }) {
  sessionStorage.setItem(AUTH_TOKEN_KEY, accessToken);
  sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_USER_KEY);
}

export function buildAuthHeaders(extraHeaders = {}) {
  const token = getStoredToken();
  const headers = {
    Accept: 'application/json',
    ...extraHeaders,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function extractErrorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload.detail === 'string') return payload.detail;
  if (payload.detail?.message) return payload.detail.message;
  if (payload.message) return payload.message;
  return fallback;
}

export async function parseApiError(response, fallback) {
  try {
    const payload = await response.json();
    return extractErrorMessage(payload, fallback);
  } catch {
    return fallback;
  }
}

export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${DEFAULT_API_BASE_URL}${path}`;
  const headers = buildAuthHeaders(options.headers || {});

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && getStoredToken()) {
    clearSession();
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      params.set('reason', 'session-expired');
      window.location.assign(`/login?${params.toString()}`);
    }
  }

  return response;
}

export async function loginRequest(username, password) {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const message = await parseApiError(
      response,
      response.status === 401
        ? 'Invalid email or password.'
        : 'Unable to sign in right now. Please try again.'
    );
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function completeOnboardingRequest({ logoFile, primaryColor }) {
  const formData = new FormData();
  formData.append('logo', logoFile);
  formData.append('primaryColor', primaryColor);

  const token = getStoredToken();
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/auth/onboarding`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to complete onboarding.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function updateBrandingRequest({ logoFile, primaryColor }) {
  const formData = new FormData();
  formData.append('primaryColor', primaryColor);
  if (logoFile) {
    formData.append('logo', logoFile);
  }

  const token = getStoredToken();
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/auth/branding`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to update branding.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function fetchAccounts() {
  const response = await apiFetch('/api/admin/accounts');
  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to load accounts.');
    throw new Error(message);
  }
  return response.json();
}

export async function createAccountRequest({ name, email, displayName, seedDemoData }) {
  const response = await apiFetch('/api/admin/accounts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      email,
      displayName: displayName || undefined,
      seedDemoData: Boolean(seedDemoData),
    }),
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to create account.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function updateAccountRequest(accountId, payload) {
  const response = await apiFetch(`/api/admin/accounts/${accountId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to update account.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function deleteAccountRequest(accountId) {
  const response = await apiFetch(`/api/admin/accounts/${accountId}`, {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 204) {
    const message = await parseApiError(response, 'Unable to delete account.');
    throw new Error(message);
  }
}

export async function resetAccountPasswordRequest(accountId) {
  const response = await apiFetch(`/api/admin/accounts/${accountId}/reset-password`, {
    method: 'POST',
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to reset password.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export function resolveAssetUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${DEFAULT_API_BASE_URL}${path}`;
}

export async function logoutRequest() {
  const response = await apiFetch('/api/auth/logout', { method: 'POST' });
  if (!response.ok && response.status !== 401) {
    const message = await parseApiError(response, 'Unable to sign out right now.');
    throw new Error(message);
  }
}

export async function fetchCurrentUser() {
  const response = await apiFetch('/api/auth/me');
  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to verify your session.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

export async function changePasswordRequest({ currentPassword, newPassword }) {
  const response = await apiFetch('/api/auth/change-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      currentPassword,
      newPassword,
    }),
  });

  if (!response.ok) {
    const message = await parseApiError(response, 'Unable to update password.');
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}